// Firebase configuration
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';

import {
  getFirestore,
  connectFirestoreEmulator,
  initializeFirestore,
  type Firestore,
} from 'firebase/firestore';

import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from 'firebase/storage';

const firebaseConfig = {
  apiKey: 'AIzaSyCaV3c4TcabWgT6-uymaig6PgGNGB-i2ZE',
  authDomain: 'plot-my-travel.firebaseapp.com',
  projectId: 'plot-my-travel',
  storageBucket: 'plot-my-travel.firebasestorage.app',
  messagingSenderId: '803909678136',
  appId: '1:803909678136:web:a0206b18bb8a0f8f9962cd',
  measurementId: 'G-TS05WHDD8L',
};

const app =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

// Initialize Firebase Authentication (this should always work)
export const auth = getAuth(app);

// Development environment setup
const isDevelopment = import.meta.env.DEV;
const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

// Initialize Firestore with enhanced error handling and fallback
let db: Firestore | null = null;

// Synchronous initialization for immediate availability
try {
  db = getFirestore(app);

  // Only connect to emulator if explicitly enabled and db is available
  if (isDevelopment && useEmulators && db) {
    try {
      connectFirestoreEmulator(db, 'localhost', 8082);
      debugLog('debug', '🔧 Connected to Firestore emulator on port 8082');
    } catch (emulatorError) {
      debugLog('debug', '⚠️ Could not connect to Firestore emulator:', emulatorError);
    }
  }
} catch (error) {
  debugLog('debug', '⚠️ getFirestore failed, trying initializeFirestore...', error);

  try {
    // Fallback: try initializeFirestore with explicit settings
    db = initializeFirestore(app, {
      experimentalForceLongPolling: false, // Disable long polling
    });
    debugLog('debug', '🔥 Firestore initialized successfully with initializeFirestore');

    // Only connect to emulator if explicitly enabled and db is available
    if (isDevelopment && useEmulators && db) {
      try {
        connectFirestoreEmulator(db, 'localhost', 8082);
        debugLog('debug', '🔧 Connected to Firestore emulator on port 8082');
      } catch (emulatorError) {
        debugLog('debug', '⚠️ Could not connect to Firestore emulator:', emulatorError);
      }
    }
  } catch (initError) {
    debugLog('debug', '❌ Both Firestore initialization methods failed:', initError);
    db = null;
  }
}

export { db };

// Initialize functions conditionally
let functions: Functions | null = null;
try {
  functions = getFunctions(app);

  // Only connect to emulator if explicitly enabled
  if (isDevelopment && useEmulators && functions) {
    try {
      connectFunctionsEmulator(functions, 'localhost', 5001);
      debugLog('debug', '🔧 Connected to Functions emulator on port 5001');
    } catch (emulatorError) {
      debugLog('debug', '⚠️ Could not connect to Functions emulator:', emulatorError);
    }
  }
} catch (error) {
  debugLog('debug', '⚠️ Firebase Functions not available:', error);
  debugLog('debug', '💡 Functions may not be enabled in your Firebase project');
}

export { functions };

// Initialize storage conditionally
let storage: FirebaseStorage | null = null;
try {
  storage = getStorage(app);

  // Only connect to emulator if explicitly enabled
  if (isDevelopment && useEmulators && storage) {
    try {
      connectStorageEmulator(storage, 'localhost', 9199);
      debugLog('debug', '🔧 Connected to Storage emulator on port 9199');
    } catch (emulatorError) {
      debugLog('debug', '⚠️ Could not connect to Storage emulator:', emulatorError);
    }
  }
} catch (error) {
  debugLog('debug', '⚠️ Firebase Storage not available:', error);
  debugLog('debug', '💡 Storage may not be enabled in your Firebase project');
}

export { storage };

// Connect Auth emulator only if explicitly enabled
if (isDevelopment && useEmulators) {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', {
      disableWarnings: true,
    });
    debugLog('debug', '🔧 Connected to Auth emulator on port 9099');
  } catch (error) {
    debugLog('debug', '⚠️ Could not connect to Auth emulator:', error);
  }
}

// Export the app instance and analytics/performance
export { app };
export default app;

import { Timestamp } from 'firebase/firestore'; // Import if using Firestore Timestamp
import debugLog from '@/lib/debug';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toSafeDate = (value: any) => {
  // Check if it's already a Date object
  if (value instanceof Date) {
    return value;
  } 

  // Check if it's a Firestore Timestamp object
  if (value instanceof Timestamp) {
    return value.toDate(); // Convert Firestore Timestamp to Date object
  }

  // Handle other cases (e.g., number representing milliseconds since epoch)
  // You might want to add more checks here if needed
  if (typeof value === 'number') {
    return new Date(value); // Create Date object from milliseconds
  }

  // If it's neither a Date, Timestamp, nor a number, return null or throw an error
  // depending on your desired error handling
  debugLog('warn', "toSafeDate() received an unsupported value:", value);
  return null; 
}

// Type exports for convenience
export type { Firestore } from 'firebase/firestore';
export type { Auth } from 'firebase/auth';
export type { FirebaseStorage } from 'firebase/storage';
export type { Functions } from 'firebase/functions';
export type { Analytics } from 'firebase/analytics';

// Nullable types for services that might not be available
export type NullableFirestore = Firestore | null;
export type NullableFirebaseStorage = FirebaseStorage | null;
export type NullableFunctions = Functions | null;
